import React, { useContext, useEffect, useState, useRef } from 'react'
import { AiFillWarning } from '@react-icons/all-files/ai/AiFillWarning'
import { useDispatch, useSelector } from 'react-redux'

import AddressBar from 'components/common/addressBar/AddressBar'
import Button from 'components/common/button'
import StoreMap from 'components/common/pickup/StoreMap'
import { Spinner } from 'components/common/spinner'
import TnpChange from 'components/tnpChange'
import { LanguageContext } from 'context/LanguageContext'
import { LayoutContext } from 'context/LayoutContext'
import { TermsContext } from 'context/TermsContext'
import { 
    trackCateringLocationSelectionFteDifferentStoreSelected,
    trackCateringLocationSelectionFteDefaultKept,
    trackCateringLocationSelectionFteSelectorOpened,
    trackCateringLocationSelectionFteSelectorExited
} from 'middleware/helper'
import { patchUpdatedTermsAndPrivacy } from 'modules/authentication/actions'
import { clearBasket, reorder } from 'modules/basket/actions'
import { setIsReOrder } from 'modules/orders/actions'
import { getConfiguration } from 'modules/configuration/actions'
import {
    getStoreDetails,
    getPickupStores,
    setSavedAddresses,
    setTempStoreDetails,
    setTempAddress,
    filterStoresByState,
    resetStoreDetails,
    setClearCartMessage,
} from 'modules/location/actions'
import { detailsFromPlaceId, detailsFromCoordinates } from 'modules/location/sagas'
import { getCurrentOrdersList } from 'modules/orders/actions'
import { setUserProfile } from 'modules/user/actions'
import { getUserProfileAsync } from 'modules/user/sagas'
import { util } from 'utils'
import { orderTypes, getJWTDetails, isEqual, isDeliveryMode, isEmpty } from 'utils/appUtils'
import constants from 'utils/constants'
import { saveCateringData } from 'utils/cateringStorage'
import BasketUpdateDialog from '../basketUpdateWarningAlert'
import Util from 'utils/Util'
import { useLocation, useNavigate } from 'react-router-dom'

const ConfirmStore = ({
    pickupAddress,
    setPickupAddress,
    nextForm,
    formState,
    children,
    prevForm,
    hide,
    isRegistration,
    setShowRegister,
    setShouldTransferTemp,
    checkout,
    setShowDateTimePicker
}) => {
    const { strings } = useContext(LanguageContext)

    const dispatch = useDispatch()
    const locationData = useLocation()
    const navigate = useNavigate()
    const { panelOpen } = useContext(LayoutContext)
    const { manageModal } = useContext(TermsContext)
    
    // All useSelector hooks must be called before useState hooks
    const isReOrder = useSelector(state => state.orders.get('isReOrder'))
    const selectedAddressInStore = useSelector(state => state.location.getIn(['selectedAddress']))
    const hasPlatterItems = useSelector(state => state.basket.get('hasPlatterItems'))
    const appSettingsIsCateringMode = useSelector(state => state.appSettings.get('isCateringMode'))
    const cateringData = useSelector(state => state.catering)
    const pickupStoresInStore = useSelector(state => state.location.getIn(['pickupStores']))
    const basketItems = useSelector(state => state.basket.get('basketItems'))
    const storeNearUser = useSelector(state => state.location.getIn(['storeNearUser']))
    const existingStore = useSelector(state => state.location.get('selectedStore'))
    const getCartInProgress = useSelector(state => state.basket.get('getCartInProgress'))
    const patchInProgress = useSelector(state => state.basket.get('cartPatchInProgress'))
    const homePageItemsInProgress = useSelector(
        state => state.products['getHomePageItemsInProgress']
    )
    const gettingStoreDetails = useSelector(state => state.location.getIn(['gettingStoreDetails']))
    
    // Now all useState hooks
    const [loader, setLoader] = useState(false)
    const [selectedStore, setSelectedStore] = useState(null)
    const [termsChanged, setTermsChanged] = useState(false)
    const [tncArr, setTncArr] = useState([])
    const [showClearCartMsg, setShowClearCartMsg] = useState(false)
    const [enteredStore, setEnteredStore] = useState(null)
    
    // Tracking refs for selector opened/exited
    const modalOpenTimeRef = useRef(null)
    const hasTrackedModalOpen = useRef(false)
    
    // Derived values (non-hooks)
    const isCateringMode = hasPlatterItems || appSettingsIsCateringMode
    const enterAddressCheck = formState === constants.enterPickUpAddressStep
    const selectedAddress = isCateringMode 
        ? cateringData?.address 
        : (selectedAddressInStore?.toJS() || null)
    const selectStore = formState === constants.confirmPickUpAddressStep
    const itemsAddedToCart = basketItems?.toJS() || null
    const { isSignedIn } = getJWTDetails()
    const shouldShowPreOrder = (!isDeliveryMode() && Util.checkForPreOrderItems(itemsAddedToCart))

    let enteredStoreId = null
    if (pickupStoresInStore) {
        pickupStoresInStore.forEach(pickupStore => {
            if (
                selectedAddress?.lattitude == pickupStore?.lat &&
                selectedAddress?.longitude == pickupStore?.lon
            ) {
                enteredStoreId = pickupStore.store_id
            }
        })
    }

    const storeId = existingStore ? isCateringMode ? cateringData?.store?.store_id : existingStore.toJS().store_id : null

    useEffect(() => {
        if (pickupStoresInStore.length > 0) {
            setLoader(false)
        }
    }, [pickupStoresInStore])
    
    // Track selector opened for catering when modal opens
    useEffect(() => {
        if (selectStore && isCateringMode && !hasTrackedModalOpen.current) {
            modalOpenTimeRef.current = Date.now()
            const previousOrderType = isDeliveryMode() ? 'delivery' : 'pickup'
            const currentLocation = selectedAddress?.formatted_address || cateringData?.address?.formatted_address || ''
            
            trackCateringLocationSelectionFteSelectorOpened(previousOrderType, currentLocation)
            hasTrackedModalOpen.current = true
        }
        
        // Reset tracking flag when modal closes
        if (!selectStore) {
            hasTrackedModalOpen.current = false
        }
    }, [selectStore, isCateringMode])

    const selectAddressFromList = suggestion => {
        setLoader(true)
        detailsFromPlaceId(suggestion.place_id, orderTypes.PICKUP).then(parsedAddress => {
            setPickupAddress({ ...pickupAddress, ...parsedAddress })
            dispatch(getPickupStores(parsedAddress?.country))
            dispatch(filterStoresByState(parsedAddress))
            setEnteredStore(parsedAddress)
        })
        enterAddressCheck && nextForm()
    }

    useEffect(() => {
        if (isEqual(gettingStoreDetails, false) && !isEqual(isRegistration, true)) {
            setLoader(false)
            dispatch(resetStoreDetails())
            
            // Trigger DateTimePicker after store details are fetched in catering mode
            if (isCateringMode && setShowDateTimePicker) {
                setShowDateTimePicker(true)
            }
            
            // Close the modal - DateTimePicker will show separately
            hide()
        }
    }, [gettingStoreDetails, isReOrder, isCateringMode, locationData.state])

    useEffect(()=>{
        return ()=>{
         dispatch(setClearCartMessage(false))
        } 
     },[])

    const selectAddressFromSavedList = suggestion => {
        setPickupAddress(suggestion)
        // need to call get pick up stroes with the saved list country
        enterAddressCheck && nextForm()
    }

    const selectAddressFromGeoLocation = coordinates => {
        setLoader(true)
        detailsFromCoordinates(coordinates, orderTypes.PICKUP).then(parsedAddress => {
            setPickupAddress({ ...pickupAddress, ...parsedAddress })
            dispatch(filterStoresByState(parsedAddress))
            setLoader(false)
        })
        enterAddressCheck && nextForm()
    }

    const handleGetUserProfile = () => {
        getUserProfileAsync()
            .then(res => {
                const { saved_address, profile } = res
                if (profile.required_terms.length) {
                    const tncArr = []
                    for (let element of profile.required_terms) {
                        if (element.type === 'privacy' || element.type === 'terms') {
                            tncArr.push(element)
                        }
                    }
                    if (tncArr.length) {
                        setTncArr(tncArr)
                        setTermsChanged(true)
                        setShouldTransferTemp(false)
                    }
                } else {
                    hide()
                    dispatch(getStoreDetails(selectedStore, false))
                }
                if (saved_address) {
                    const parsedSavedAddress = saved_address.map(each =>
                        util.getCommonFormattedAddress(each)
                    )
                    dispatch(setSavedAddresses(parsedSavedAddress))
                } else {
                    dispatch(setSavedAddresses([]))
                }
                const parsedProfile = util.parseUserProfile(profile)
                dispatch(setUserProfile(parsedProfile))
                setLoader(false)
            })
            .catch(e => {
                console.log('here for error', e)
            })
    }

    const handleSelectStore = () => {
        setLoader(true)
        if(isCateringMode){
            // Track catering store selection
            const previousOrderType = isDeliveryMode() ? 'delivery' : 'pickup'
            const isDefaultStore = existingStore?.toJS()?.store_id === selectedStore?.store_id
            
            if (isDefaultStore) {
                trackCateringLocationSelectionFteDefaultKept(
                    selectedStore?.store_id,
                    selectedStore?.name,
                    previousOrderType
                )
            } else {
                trackCateringLocationSelectionFteDifferentStoreSelected(
                    selectedStore?.store_id,
                    selectedStore?.name,
                    previousOrderType,
                    !isDefaultStore
                )
            }
            
            saveCateringData(pickupAddress, null, selectedStore)
            dispatch(getStoreDetails(selectedStore, false))
            // Note: DateTimePicker trigger moved to useEffect that watches for store details completion
        }
        else {
            if (isSignedIn && pickupAddress?.country !== selectedAddress?.country) {
                dispatch(setTempAddress(pickupAddress))
                dispatch(setTempStoreDetails(selectedStore))
                dispatch(getStoreDetails(selectedStore, true))
                handleGetUserProfile()
                manageModal(false)
                dispatch(getCurrentOrdersList())
                dispatch(getConfiguration())
            } else {
                if (isRegistration) {
                    dispatch(getStoreDetails(selectedStore, false))
                    nextForm()
                } else {
                    if (pickupAddress?.country !== selectedAddress?.country) {
                        dispatch(getConfiguration(pickupAddress.country))
                    }
                    dispatch(getStoreDetails(selectedStore, false))
                    dispatch(setTempStoreDetails(null))
                    dispatch(setTempAddress(null))
                }
            }
        }
        
    }

    const closeOptions = () => {
        // Track selector exited for catering
        if (isCateringMode && hasTrackedModalOpen.current && modalOpenTimeRef.current) {
            const timeInSelector = Math.floor((Date.now() - modalOpenTimeRef.current) / 1000) // seconds
            const itemsInCart = itemsAddedToCart?.length || 0
            const previousOrderType = isDeliveryMode() ? 'delivery' : 'pickup'
            
            trackCateringLocationSelectionFteSelectorExited(itemsInCart, timeInSelector, previousOrderType)
            hasTrackedModalOpen.current = false
            modalOpenTimeRef.current = null
        }
        
        // Reset reorder flag when user cancels
        if (isReOrder) {
            dispatch(setIsReOrder(false))
        }
        panelOpen && setShowRegister ? setShowRegister(false) : hide()
    }

    const handleCheckCart = () => {
        // Check for catering mode
        if (isCateringMode) {
            if (itemsAddedToCart.length > 0 &&
                (selectedStore ? selectedStore.store_id !== storeId : enteredStoreId !== storeId)) {
                setShowClearCartMsg(true)
                dispatch(setClearCartMessage(true))
                return
            } else {
                handleSelectStore()
                return
            }
        }
        
        // Check for regular mode
        if (
            itemsAddedToCart.length > 0 &&
            (selectedStore ? selectedStore.store_id !== storeId : enteredStoreId !== storeId)
        ) {
            setShowClearCartMsg(true)
            dispatch(setClearCartMessage(true))
            return
        }
        handleSelectStore()
    }

    const clearCompleteBasket = () => {
        dispatch(clearBasket())
        handleSelectStore()
        setShowClearCartMsg(false)
        dispatch(setClearCartMessage(false))
    }

    const hideFeedback = () => {
        setShowClearCartMsg(false)
        dispatch(setClearCartMessage(false))
    }

    const handleConfirmLocationAtTNC = (termId, privacyId) => {
        setShouldTransferTemp(true)
        setTimeout(() => {
            const updatedTermsAndPrivacy = [
                ...(termId ? [termId.id] : []),
                ...(privacyId ? [privacyId.id] : []),
            ]
            dispatch(patchUpdatedTermsAndPrivacy(updatedTermsAndPrivacy))
            dispatch(getStoreDetails(selectedStore, false))
            hide()
        }, 0)
    }

    if (loader) {
        return <div className={'enterAddressForm'} style={{justifyContent: 'center'}}><Spinner height={'5rem'} width={'5rem'} /></div>
    }

    if (getCartInProgress || patchInProgress || homePageItemsInProgress) {
        return <div className={'enterAddressForm'} style={{justifyContent: 'center'}}><Spinner height={'5rem'} width={'5rem'} /></div>
    }

    if (showClearCartMsg) {
        if (checkout && shouldShowPreOrder) {
            handleSelectStore()
            setShowClearCartMsg(false)
            return null
        }
        return (
            <div
                className={'feedBackForm'}
                tabIndex="0"
                aria-label={`${strings.basketItemsRemovalWarning} ${strings.basketItemsRemovalDetailedWarning} `}
            >
                <div className={'feedBackForm__content'}>
                    <AiFillWarning className={'feedBackForm__content-warningIcon'} />
                    <p className={'feedBackForm__content-warningMsg1'}>
                        {strings.basketItemsRemovalWarning}
                    </p>
                    <p className={'feedBackForm__content-warningMsg2'}>
                        {strings.storeChangeClearCartMsg}
                    </p>
                </div>
                <div className={'feedBackForm__action'}>
                    <Button
                        className={'cancel cancel-lg'}
                        onClick={hideFeedback}
                        aria-labelledby="cancel button"
                    >
                        {strings.cancelButton}
                    </Button>
                    <Button
                        className={'confirm confirm-lg'}
                        onClick={clearCompleteBasket}
                        aria-labelledby="clear basket button"
                    >
                        {strings.clearBasket}
                    </Button>
                </div>
            </div>
        )
    }

    return (
        <div className={'enterAddressForm'}>
            {termsChanged ? (
                <TnpChange
                    tncArr={tncArr}
                    handleConfirmLocationAtTNC={handleConfirmLocationAtTNC}
                />
            ) : (
                <>
                    <p className={'enterAddressForm-heading'}>
                        {selectStore ? strings.confirmPickupTitle : strings.locationTitle}
                    </p>
                    <p className={'enterAddressForm-sub'}>
                        {selectStore ? strings.pickupTitle2 : strings.pickupTitle1}
                    </p>
                    {children}
                    {selectStore ? (
                        <StoreMap
                            currentAddress={pickupAddress}
                            prevForm={prevForm}
                            selectedStore={selectedStore}
                            setSelectedStore={setSelectedStore}
                            storeNearUser={Boolean(storeNearUser?.length)}
                            enteredStore={enteredStore}
                        />
                    ) : (
                        <AddressBar
                            id={'pickUpModal'}
                            styleChange={'modalAddressComp'}
                            selectedAddress={pickupAddress}
                            selectAddressFromList={selectAddressFromList}
                            // selectAddressFromRecentList={selectAddressFromRecentList}
                            selectAddressFromSavedList={selectAddressFromSavedList}
                            selectAddressFromGeoLocation={selectAddressFromGeoLocation}
                            isPickup={true}
                            setLoader={setLoader}
                        />
                    )}
                    {!selectStore && (
                        <div
                            className={'enterAddressForm-actionBtns enterAddressForm-actionBtns-1'}
                        >
                            <Button className={'cancel cancel-lg'} onClick={closeOptions}>
                                {strings.cancelButton}
                            </Button>
                        </div>
                    )}
                    {!enterAddressCheck && (
                        <div
                            className={`enterAddressForm-actionBtns enterAddressForm-actionBtns-2 ${
                                selectStore && 'enterAddressForm-actionBtns-confirmPickupAddress'
                            } `}
                        >
                            {selectStore ? (
                                <Button className={'cancel cancel-lg'} onClick={prevForm}>
                                     {isCateringMode 
                                        ? strings.cancelButton
                                        : strings.cancelButton}
                                </Button>
                            ) : (
                                <Button className={'cancel cancel-lg'} onClick={closeOptions}>
                                    {isCateringMode 
                                        ? strings.cancelButton 
                                        : strings.cancelButton}
                                </Button>
                            )}
                            {selectStore && (
                                <Button
                                    onClick={handleCheckCart}
                                    className={'confirm confirm-lg'}
                                    disabled={
                                        !selectedStore && selectedAddress?.type === 'delivery'
                                    }
                                >
                                    {strings.selectStoreButton}
                                </Button>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    )
}

export default ConfirmStore
